import { useEffect, useMemo, useRef, useState } from "react";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveDatePreset, type DatePreset } from "@dashboard/shared";
import type { DateRangeValue } from "@/components/DateRangePicker";
import { cn } from "@/lib/utils";

/**
 * Zeitraum-Waehler der Partneruebersicht (Operator 2026-10-09): Knopf mit Kalendersymbol, aufklappend links ein
 * Zwei-Monats-Kalender, rechts die Voreinstellungen (Seiten getauscht 2026-10-10). "Custom": erster Klick = Start, zweiter = Ende, alle Tage dazwischen
 * markiert (auch schon beim Darueberfahren), "Anwenden" uebernimmt.
 *
 * Gerechnet wird wie ueberall im Dashboard in UTC-Tagen (resolveDatePreset). Die API kennt nur die sechs gemeinsamen
 * Presets; "Vorgestern" und "Letzte 90 Tage" gehen deshalb als feste Von-bis-Daten raus (kind "custom" mit `ui`).
 */

type Option = { key: string; label: string; preset?: DatePreset; tage?: [number, number] };

const OPTIONEN: Option[] = [
  { key: "today", label: "Heute", preset: "today" },
  { key: "yesterday", label: "Gestern", preset: "yesterday" },
  { key: "dby", label: "Vorgestern", tage: [-2, -2] },
  { key: "last7", label: "Letzte 7 Tage", preset: "last7" },
  { key: "last30", label: "Letzte 30 Tage", preset: "last30" },
  { key: "mtd", label: "Dieses Monat", preset: "mtd" },
  { key: "ltd", label: "Letztes Monat", preset: "ltd" },
  { key: "last90", label: "Letzte 90 Tage", tage: [-89, 0] },
  { key: "custom", label: "Custom" },
];

const MONAT = new Intl.DateTimeFormat("de-DE", { month: "long", year: "numeric", timeZone: "UTC" });
const KURZ = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", timeZone: "UTC" });
const LANG = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
const WOCHENTAGE = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const utc = (iso: string) => new Date(`${iso}T00:00:00Z`);
const iso = (d: Date) => d.toISOString().slice(0, 10);
function plusTage(s: string, n: number): string {
  const d = utc(s); d.setUTCDate(d.getUTCDate() + n); return iso(d);
}
function plusMonate(ym: string, n: number): string {
  const [j, m] = ym.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(j, m - 1 + n, 1));
  return iso(d).slice(0, 7);
}

/** Welche Option zeigt ein Wert? */
function optionVon(v: DateRangeValue): string {
  if (v.kind === "preset") return v.preset;
  return v.ui ?? "custom";
}

/** Wert einer Option (ohne Custom). */
function wertVon(o: Option): DateRangeValue | null {
  if (o.preset) {
    const { from, to } = resolveDatePreset(o.preset);
    return { kind: "preset", preset: o.preset, from, to };
  }
  if (o.tage) {
    const heute = resolveDatePreset("today").from;
    return { kind: "custom", from: plusTage(heute, o.tage[0]), to: plusTage(heute, o.tage[1]), ui: o.key };
  }
  return null;
}

export function zeitraumText(v: DateRangeValue): string {
  return v.from === v.to ? LANG.format(utc(v.from)) : `${KURZ.format(utc(v.from))} – ${LANG.format(utc(v.to))}`;
}

export function ZeitraumWaehler({ value, onChange, className }: {
  value: DateRangeValue; onChange: (v: DateRangeValue) => void; className?: string;
}) {
  const [offen, setOffen] = useState(false);
  const [gewaehlt, setGewaehlt] = useState(optionVon(value));
  // Custom-Auswahl: Start und (vorlaeufiges) Ende; hover zeigt die Spanne schon vor dem zweiten Klick.
  const [start, setStart] = useState<string | null>(value.from);
  const [ende, setEnde] = useState<string | null>(value.to);
  const [hover, setHover] = useState<string | null>(null);
  const heute = resolveDatePreset("today").from;
  const [monat, setMonat] = useState(() => plusMonate(value.to.slice(0, 7), -1));
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOffen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOffen(false); };
    document.addEventListener("mousedown", zu);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", zu); document.removeEventListener("keydown", esc); };
  }, [offen]);

  const oeffnen = () => {
    setGewaehlt(optionVon(value)); setStart(value.from); setEnde(value.to); setHover(null);
    setMonat(plusMonate(value.to.slice(0, 7), -1));
    setOffen((o) => !o);
  };

  const waehlen = (o: Option) => {
    setGewaehlt(o.key);
    const v = wertVon(o);
    if (!v) { setStart(null); setEnde(null); return; }          // Custom: Kalender wartet auf zwei Klicks
    setStart(v.from); setEnde(v.to);
    onChange(v); setOffen(false);
  };

  const tagKlick = (tag: string) => {
    setGewaehlt("custom");
    if (!start || ende) { setStart(tag); setEnde(null); return; }
    if (tag < start) { setEnde(start); setStart(tag); } else setEnde(tag);
  };

  const anwenden = () => {
    if (!start) return;
    onChange({ kind: "custom", from: start, to: ende ?? start });
    setOffen(false);
  };

  // Markierte Spanne: gewaehlte Tage, beim Waehlen des Endes bis zum Mauszeiger.
  const [von, bis] = useMemo(() => {
    if (!start) return [null, null] as const;
    const e = ende ?? hover ?? start;
    return e < start ? [e, start] as const : [start, e] as const;
  }, [start, ende, hover]);

  const label = OPTIONEN.find((o) => o.key === optionVon(value))?.label ?? "Custom";

  return (
    <div ref={ref} className={cn("relative", className)}>
      <button type="button" onClick={oeffnen} aria-expanded={offen} aria-haspopup="dialog" data-zeitraum
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-background px-3 text-[13px] shadow-sm hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        <CalendarDays className="h-4 w-4 text-muted-foreground" />
        <span className="font-semibold">{label}</span>
        <span className="tabular-nums text-muted-foreground">{zeitraumText(value)}</span>
        <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", offen && "rotate-180")} />
      </button>

      {offen && (
        <div role="dialog" aria-label="Zeitraum wählen" data-zeitraum-panel
          className="absolute right-0 top-full z-50 mt-2 flex w-[min(92vw,640px)] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xl sm:flex-row-reverse">
          <div className="flex shrink-0 flex-row flex-wrap gap-1 border-b border-border p-2 sm:w-44 sm:flex-col sm:flex-nowrap sm:border-b-0 sm:border-l">
            {OPTIONEN.map((o) => (
              <button key={o.key} type="button" onClick={() => waehlen(o)} data-option={o.key}
                className={cn("rounded-md px-3 py-1.5 text-left text-[13px] hover:bg-muted",
                  gewaehlt === o.key && "bg-[#eef3fd] font-semibold text-[#3563d6] hover:bg-[#e7eefc] dark:bg-[#7aa2ff]/15 dark:text-[#7aa2ff]")}>
                {o.label}
              </button>
            ))}
          </div>
          <div className="min-w-0 flex-1 p-3">
            <div className="flex items-center justify-between">
              <button type="button" onClick={() => setMonat((m) => plusMonate(m, -1))} aria-label="Früher"
                className="rounded-md p-1 text-muted-foreground hover:bg-muted"><ChevronLeft className="h-4 w-4" /></button>
              <button type="button" onClick={() => setMonat((m) => plusMonate(m, 1))} aria-label="Später"
                disabled={plusMonate(monat, 1) >= heute.slice(0, 7)}
                className="rounded-md p-1 text-muted-foreground hover:bg-muted disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <div className="-mt-6 grid gap-4 sm:grid-cols-2">
              {[monat, plusMonate(monat, 1)].map((ym, i) => (
                <Monat key={ym} ym={ym} heute={heute} von={von} bis={bis} versteckt={i === 0 ? "" : "hidden sm:block"}
                  onTag={tagKlick} onHover={(t) => (start && !ende ? setHover(t) : undefined)} />
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
              <span className="text-[12.5px] tabular-nums text-muted-foreground">
                {von && bis ? zeitraumText({ kind: "custom", from: von, to: bis }) : "Startdatum wählen"}
                {start && !ende && gewaehlt === "custom" && von ? " · Enddatum wählen" : ""}
              </span>
              <button type="button" onClick={anwenden} disabled={!start || gewaehlt !== "custom"}
                className="rounded-lg bg-[#3563d6] px-3.5 py-1.5 text-[13px] font-semibold text-white hover:bg-[#2c56c2] disabled:opacity-40">
                Anwenden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Ein Monat als Raster (Montag zuerst). Tage in der Spanne hellblau, Start und Ende als volle Kreise; Zukunft gesperrt. */
function Monat({ ym, heute, von, bis, onTag, onHover, versteckt }: {
  ym: string; heute: string; von: string | null; bis: string | null;
  onTag: (t: string) => void; onHover: (t: string | null) => void; versteckt: string;
}) {
  const erster = utc(`${ym}-01`);
  const versatz = (erster.getUTCDay() + 6) % 7;
  const tage = new Date(Date.UTC(erster.getUTCFullYear(), erster.getUTCMonth() + 1, 0)).getUTCDate();
  const zellen: (string | null)[] = [...Array(versatz).fill(null), ...Array.from({ length: tage }, (_, i) => `${ym}-${String(i + 1).padStart(2, "0")}`)];
  return (
    <div className={versteckt} onMouseLeave={() => onHover(null)}>
      <div className="mb-2 text-center text-[13px] font-semibold capitalize">{MONAT.format(erster)}</div>
      <div className="grid grid-cols-7 text-center text-[11px] text-muted-foreground">
        {WOCHENTAGE.map((w) => <div key={w} className="pb-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {zellen.map((t, i) => {
          if (!t) return <div key={`l${i}`} />;
          const zukunft = t > heute;
          const drin = von !== null && bis !== null && t >= von && t <= bis;
          const rand = t === von || t === bis;
          const links = t === von, rechts = t === bis;
          return (
            <div key={t} className={cn("relative flex h-8 items-center justify-center",
              drin && !(links && rechts) && "bg-[#eef3fd] dark:bg-[#7aa2ff]/15",
              links && !rechts && "rounded-l-full", rechts && !links && "rounded-r-full")}>
              <button type="button" disabled={zukunft} onClick={() => onTag(t)} onMouseEnter={() => onHover(t)} data-tag={t}
                className={cn("h-8 w-8 rounded-full text-[12.5px] tabular-nums",
                  rand ? "bg-[#3563d6] font-semibold text-white" : "hover:bg-muted",
                  t === heute && !rand && "font-semibold text-[#3563d6] dark:text-[#7aa2ff]",
                  zukunft && "cursor-not-allowed opacity-30 hover:bg-transparent")}>
                {Number(t.slice(8))}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
